import { Filter, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { taskFieldValue } from "@/lib/plannerPoints";
import type { PlannerTask } from "@/services/plannerService";

/**
 * Parity pass 3: the upstream condition filter — a popover of
 * [Where|And][field][operator][value] rows, AND-combined, layered under the
 * free-text search. Half-filled rows are IGNORED rather than matching
 * nothing, so the table never blanks while you are still choosing a value.
 */

export type FilterField =
  | "task_type" | "billing_period" | "status" | "service_bucket"
  | "assignee" | "due_date" | "points" | "qty" | "notes";

export interface FilterRule {
  id: string;
  field: FilterField;
  op: string;
  value: string;
}

const FIELDS: { key: FilterField; label: string; kind: "text" | "number" | "enum" | "date" }[] = [
  { key: "task_type", label: "Task Type", kind: "text" },
  { key: "billing_period", label: "Period", kind: "text" },
  { key: "status", label: "Status", kind: "enum" },
  { key: "service_bucket", label: "Bucket", kind: "text" },
  { key: "assignee", label: "Assignee", kind: "text" },
  { key: "due_date", label: "Due Date", kind: "date" },
  { key: "points", label: "Points", kind: "number" },
  { key: "qty", label: "Qty", kind: "number" },
  { key: "notes", label: "Notes", kind: "text" },
];

const OPS: Record<string, { value: string; label: string }[]> = {
  text: [
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "doesn't contain" },
    { value: "is", label: "is" },
    { value: "is_not", label: "is not" },
    { value: "empty", label: "is empty" },
    { value: "not_empty", label: "is not empty" },
  ],
  enum: [
    { value: "is", label: "is" },
    { value: "is_not", label: "is not" },
  ],
  number: [
    { value: "eq", label: "=" },
    { value: "gt", label: ">" },
    { value: "lt", label: "<" },
    { value: "gte", label: "≥" },
    { value: "lte", label: "≤" },
  ],
  date: [
    { value: "before", label: "before" },
    { value: "after", label: "after" },
    { value: "on", label: "on" },
    { value: "empty", label: "is empty" },
    { value: "not_empty", label: "is not empty" },
  ],
};

const kindOf = (f: FilterField) => FIELDS.find((x) => x.key === f)?.kind ?? "text";

/** A rule participates only when it is complete (upstream's isRuleActive). */
export function isRuleActive(r: FilterRule): boolean {
  if (!r.field || !r.op) return false;
  if (r.op === "empty" || r.op === "not_empty") return true;
  if (r.value.trim() === "") return false;
  if (kindOf(r.field) === "number" && Number.isNaN(Number(r.value))) return false;
  return true;
}

function valueOf(t: PlannerTask, f: FilterField): string {
  const raw = taskFieldValue(t, f); // "points" is the row total, as shown
  return raw == null ? "" : String(raw);
}

export function applyRules(tasks: PlannerTask[], rules: FilterRule[]): PlannerTask[] {
  const active = rules.filter(isRuleActive);
  if (active.length === 0) return tasks;
  return tasks.filter((t) =>
    active.every((r) => {
      const v = valueOf(t, r.field);
      const q = r.value.trim();
      switch (r.op) {
        case "contains": return v.toLowerCase().includes(q.toLowerCase());
        case "not_contains": return !v.toLowerCase().includes(q.toLowerCase());
        case "is": return v.toLowerCase() === q.toLowerCase();
        case "is_not": return v.toLowerCase() !== q.toLowerCase();
        case "empty": return v.trim() === "";
        case "not_empty": return v.trim() !== "";
        case "eq": return Number(v) === Number(q);
        case "gt": return Number(v) > Number(q);
        case "lt": return Number(v) < Number(q);
        case "gte": return Number(v) >= Number(q);
        case "lte": return Number(v) <= Number(q);
        case "before": return v !== "" && v < q;
        case "after": return v !== "" && v > q;
        case "on": return v === q;
        default: return true;
      }
    }),
  );
}

export function TaskFilter({
  rules,
  onChange,
}: {
  rules: FilterRule[];
  onChange: (rules: FilterRule[]) => void;
}) {
  const activeCount = rules.filter(isRuleActive).length;

  const add = () =>
    onChange([
      ...rules,
      { id: `r${Date.now()}`, field: "status", op: "is", value: "" },
    ]);
  const patch = (id: string, p: Partial<FilterRule>) =>
    onChange(rules.map((r) => (r.id === id ? { ...r, ...p } : r)));
  const remove = (id: string) => onChange(rules.filter((r) => r.id !== id));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1">
          <Filter className="h-3.5 w-3.5" />
          Filter
          {activeCount > 0 ? (
            <span className="ml-1 rounded-full bg-coral px-1.5 text-[10px] font-semibold text-white">
              {activeCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[520px] p-0">
        <div className="flex items-center justify-between border-b border-border px-4 py-2">
          <span className="text-xs font-medium uppercase tracking-wide text-foreground">
            Filters {activeCount > 0 ? `(${activeCount})` : ""}
          </span>
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Clear all
          </button>
        </div>

        <div className="max-h-[60vh] space-y-2 overflow-y-auto p-3">
          {rules.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">No filters yet. Add one below.</p>
          ) : (
            rules.map((r, i) => {
              const kind = kindOf(r.field);
              return (
                <div key={r.id} className="flex items-center gap-2">
                  <span className="w-12 shrink-0 text-xs text-muted-foreground">
                    {i === 0 ? "Where" : "And"}
                  </span>
                  <Select
                    value={r.field}
                    onValueChange={(v) => {
                      const nk = kindOf(v as FilterField);
                      patch(r.id, { field: v as FilterField, op: OPS[nk][0].value, value: "" });
                    }}
                  >
                    <SelectTrigger className="h-8 w-32 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELDS.map((f) => (
                        <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={r.op} onValueChange={(v) => patch(r.id, { op: v })}>
                    <SelectTrigger className="h-8 w-32 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {OPS[kind].map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {r.op === "empty" || r.op === "not_empty" ? (
                    <div className="h-8 flex-1" />
                  ) : kind === "enum" ? (
                    <Select value={r.value || undefined} onValueChange={(v) => patch(r.id, { value: v })}>
                      <SelectTrigger className="h-8 flex-1 text-sm">
                        <SelectValue placeholder="— pick value —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started">Pending</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      className="h-8 flex-1 text-sm"
                      type={kind === "number" ? "number" : kind === "date" ? "date" : "text"}
                      value={r.value}
                      onChange={(e) => patch(r.id, { value: e.target.value })}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => remove(r.id)}
                    aria-label="remove condition"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-border bg-muted/30 px-4 py-3">
          <button
            type="button"
            onClick={add}
            className="flex items-center gap-1 text-sm font-medium text-coral-dark hover:underline"
          >
            <Plus className="h-3.5 w-3.5" /> Add condition
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default TaskFilter;
